<template>
  <nav class="tabbar" aria-label="Hauptbereiche" :style="gridStyle">
    <router-link
      v-for="target in targets"
      :key="target.path"
      :to="target.path"
      class="tabbar__item"
      :class="{ 'tabbar__item--here': isActive(target) }"
      :aria-current="isActive(target) ? 'page' : null"
      @click.native="select(target)"
    >
      <app-icon :name="target.icon" :size="22" class="tabbar__icon" />
      <span class="tabbar__label">{{ target.title }}</span>
    </router-link>
    <button
      type="button"
      class="tabbar__item"
      :class="{ 'tabbar__item--here': moreActive }"
      :aria-expanded="moreOpen ? 'true' : 'false'"
      @click="$emit('more')"
    >
      <app-icon name="menu" :size="22" class="tabbar__icon" />
      <span class="tabbar__label">Mehr</span>
    </button>
  </nav>
</template>

<script>
// Handy: untere Navigation (Spezifikation 2.11, gen_r3.py:1554–1577). Ziele Übersicht · Verkäufe · Live-Tracking
// (nur, wenn in der Navigation sichtbar) · Mehr. Aktiv ist Mehr, wenn das Mehr-Menü offen ist oder die Route keines
// der ersten Ziele ist; sonst das passende Ziel. Ereignisse: select ({ target, samePage }: Ziel angetippt; samePage =
// Ziel ist die bisherige Seite, also kein Routenwechsel), more (Mehr angetippt).
import { normalizePath } from '../../shell/navigation';

const TARGETS = [
  { title: 'Dashboard', icon: 'grid', path: '/overview' },
  { title: 'Verkäufe', icon: 'receipt', path: '/administration/ticketsales' },
  { title: 'Live-Tracking', icon: 'pin', path: '/analytics/live' }
];

export default {
  name: 'AppTabBar',
  props: {
    groups: { type: Array, required: true },
    activePath: { type: String, default: '' }
  },
  computed: {
    visiblePaths() {
      const paths = {};
      this.groups.forEach((group) => {
        if (group.path) {
          paths[normalizePath(group.path)] = true;
        }
        (group.children || []).forEach((entry) => {
          paths[normalizePath(entry.path)] = true;
        });
      });
      return paths;
    },
    targets() {
      return TARGETS.filter(target => this.visiblePaths[normalizePath(target.path)]);
    },
    moreOpen() {
      return !!(this.$shell && this.$shell.moreOpen);
    },
    moreActive() {
      return this.moreOpen || !this.targets.some(target => normalizePath(target.path) === this.activePath);
    },
    gridStyle() {
      return { gridTemplateColumns: 'repeat(' + (this.targets.length + 1) + ', minmax(0, 1fr))' };
    }
  },
  methods: {
    isActive(target) {
      return !this.moreActive && normalizePath(target.path) === this.activePath;
    },
    select(target) {
      // activePath ist hier noch der Pfad vor dem Antippen (Props folgen erst beim nächsten Rendern)
      this.$emit('select', { target, samePage: normalizePath(target.path) === this.activePath });
    }
  }
};
</script>

<style scoped>
.tabbar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5;
  display: grid;
  justify-items: center;
  padding: 4px max(8px, env(safe-area-inset-right, 0px)) max(12px, env(safe-area-inset-bottom, 0px)) max(8px, env(safe-area-inset-left, 0px));
  background: var(--surface);
  border-top: 1px solid var(--line);
  font-family: var(--font);
}
.tabbar__item {
  width: 80px;
  max-width: 100%;
  height: 48px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin: 0;
  padding: 0;
  border: 1px solid transparent;
  border-radius: var(--r-ctl);
  background: transparent;
  color: var(--ink-2);
  font-family: var(--font);
  font-size: 11px;
  line-height: 14px;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.tabbar__icon {
  flex: none;
  color: var(--ink-3);
}
.tabbar__label {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* aktiv: graue Kachel (--here; dunkel heller als die Leiste) ohne Kante und Schatten wie in der Seitenleiste, Icon
   orange (Ort), Label --ink fett (Kundenwunsch 12.10.2026) */
.tabbar__item.tabbar__item--here {
  background: var(--here);
  color: var(--ink);
  font-weight: 700;
}
.tabbar__item.tabbar__item--here .tabbar__icon {
  color: var(--acc);
}
/* gedrückt (Hover gibt es auf dem Handy nicht) */
.tabbar__item:active {
  background: var(--press);
}
</style>
